// ============================================================
// SUPPLIER COST DOCUMENTS TYPES
// ============================================================

// Re-export document types from receipts feature
export type { DocumentType, OcrStatus, DocumentSource } from '@/features/receipts/types';
export {
  DOCUMENT_TYPE_CONFIG,
  OCR_STATUS_CONFIG,
  RECEIPT_ACCEPTED_TYPES,
} from '@/features/receipts/types';

/**
 * Supplier cost document type options for UI selectors
 */
export const SUPPLIER_COST_DOCUMENT_TYPES: {
  value: 'receipt' | 'expense_invoice';
  label: string;
}[] = [
  { value: 'receipt', label: 'Receipt' },
  { value: 'expense_invoice', label: 'Invoice' },
];

/**
 * Supplier cost document from view with joined context
 */
export interface SupplierCostDocumentView {
  id: string;
  businessId: string;
  projectId: string | null;
  supplierId: string | null;
  uploadedBy: string;
  uploadedAt: string;
  filePath: string;
  mimeType: string | null;
  documentType: 'receipt' | 'expense_invoice';
  source: 'web' | 'mobile';
  vendorNameRaw: string | null;
  documentDate: string | null;
  currency: string | null;
  totalAmount: number | null;
  taxAmount: number | null;
  ocrStatus: string;
  ocrLastAttemptAt: string | null;
  ocrErrorMessage: string | null;
  ocrProvider: string | null;
  latestOcrCallId: string | null;
  createdAt: string;
  updatedAt: string;
  // Computed flags
  hasProject: boolean;
  // Supplier context
  supplierName: string | null;
  // Project context
  projectName: string | null;
  projectStatus: string | null;
  // Customer context
  customerFirstName: string | null;
  customerLastName: string | null;
  customerName: string | null;
  // Uploader context
  uploadedByName: string | null;
  // Business context
  businessName: string | null;
}

// ============================================================
// MAPPING HELPERS
// ============================================================

/**
 * Map database row to SupplierCostDocumentView type
 */
export function mapSupplierCostDocumentViewFromDb(row: Record<string, unknown>): SupplierCostDocumentView {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    projectId: row.project_id as string | null,
    supplierId: row.supplier_id as string | null,
    uploadedBy: row.uploaded_by as string,
    uploadedAt: row.uploaded_at as string,
    filePath: row.file_path as string,
    mimeType: row.mime_type as string | null,
    documentType: row.document_type as 'receipt' | 'expense_invoice',
    source: row.source as 'web' | 'mobile',
    vendorNameRaw: row.vendor_name_raw as string | null,
    documentDate: row.document_date as string | null,
    currency: row.currency as string | null,
    totalAmount: row.total_amount as number | null,
    taxAmount: row.tax_amount as number | null,
    ocrStatus: row.ocr_status as string,
    ocrLastAttemptAt: row.ocr_last_attempt_at as string | null,
    ocrErrorMessage: row.ocr_error_message as string | null,
    ocrProvider: row.ocr_provider as string | null,
    latestOcrCallId: row.latest_ocr_call_id as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    hasProject: row.has_project as boolean,
    supplierName: row.supplier_name as string | null,
    projectName: row.project_name as string | null,
    projectStatus: row.project_status as string | null,
    customerFirstName: row.customer_first_name as string | null,
    customerLastName: row.customer_last_name as string | null,
    customerName: row.customer_name as string | null,
    uploadedByName: row.uploaded_by_name as string | null,
    businessName: row.business_name as string | null,
  };
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number | null, currency: string | null): string {
  if (amount === null) return '-';
  const currencyCode = currency || 'CAD';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
}
